import { Inject, Injectable, Optional } from '@angular/core';

import { CookieService } from '../cookie/cookie.service';
import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { SERVER_REQUEST, SERVER_RESPONSE, ServerRequest, ServerResponse } from '../server/server.tokens';
import { WindowRefService } from '../window-ref/window-ref.service';

export const REDIRECT_COOKIE = 'hdms-redirect';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private readonly win: Window;
  private readonly redirectHostPattern = this.createHostRegexList(this.env);
  private blockSubsequent = false;
  private redirectUrl: string;

  constructor(
    @Optional() @Inject(SERVER_REQUEST) private readonly request: ServerRequest,
    @Optional() @Inject(SERVER_RESPONSE) private readonly response: ServerResponse,
    private readonly cookieService: CookieService,
    private readonly platform: PlatformService,
    private readonly env: EnvironmentService,
    { nativeWindow }: WindowRefService
  ) {
    this.win = nativeWindow;
  }

  /**
   * Redirect on server or client, including redirect-loop prevention.
   * Loop prevention is required in case of automated redirects e.g.
   * by evaluating login status and redirecting to SSO.
   * If a login attempt fails for unknown reason, SSO still would
   * redirect back to current page, leading to a loop.
   * Prevention is mainly done by checking the referrer, additionally
   * using a temporary cookie for bypassing referrer check in order
   * to allow page reloads for re-triggering the redirect.
   * @param url
   * @param preventRedirectLoop
   * @param blockSubsequent
   */
  redirectTo(url: string, preventRedirectLoop = false, blockSubsequent = false): void {
    if (this.blockSubsequent) {
      return;
    }

    if (this.urlDiffers(url) && (!preventRedirectLoop || this.canRedirect())) {
      this.blockSubsequent = blockSubsequent;
      this.cookieService.delete(REDIRECT_COOKIE);
      this.redirectUrl = url;

      if (this.platform.isServer) {
        this.response.status(302);
        this.response.setHeader('Location', url);
      } else {
        this.win.location.assign(url);
      }
    } else if (this.platform.isBrowser) {
      this.cookieService.set(REDIRECT_COOKIE, this.getCurrentPath());
    }
  }

  /**
   * Conditions for allowing redirect are:
   * - referrer doesn't match with redirectReferrer regexes
   * - presence of redirect cookie, set after returning from a preceding redirect to same page
   */
  canRedirect(): boolean {
    const isRedirected = this.cookieService.get(REDIRECT_COOKIE) === this.getCurrentPath();
    const referrer = this.platform.isServer ? this.request?.headers.referer : this.win.document.referrer;

    return isRedirected || !this.redirectHostPattern.some(regex => regex.test(referrer));
  }

  /**
   * Indicate there's a redirect in progress
   */
  get isRedirecting(): boolean {
    return !!this.redirectUrl;
  }

  /**
   * Prevent invoking redirects to same url multiple times
   * @param url
   * @private
   */
  private urlDiffers(url: string): boolean {
    return this.redirectUrl !== url;
  }

  private getCurrentPath(): string {
    return this.request?.originalUrl || this.win.location.pathname;
  }

  private createHostRegexList({ redirectHostPattern }: EnvironmentService): RegExp[] {
    return redirectHostPattern ? redirectHostPattern.split(';').map(r => new RegExp(r.trim())) : [];
  }
}
